import React from "react";

const Overview: React.FC = () => {
  return (
    <section id="overview" className="scroll-mt-20">
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
        Overview
      </h2>
      <div className="mt-4 prose prose-slate max-w-none">
        <p className="text-slate-600">
          Welcome to the Zynth.ai API Documentation! Here you'll find a complete
          guide to integrating Zynth's powerful AI-driven presentation
          capabilities into your own apps and workflows.
        </p>

        <p className="mt-4 text-slate-600">
          Zynth API gives developers and product teams the power of AI-generated
          business presentations and slides at scale. The Zynth API makes it
          effortless to:
        </p>

        <ul className="mt-4 space-y-2 text-slate-600">
          <li>
            • Generate presentations using AI through a unified API — from pitch
            decks to project reports.
          </li>
          <li>
            • Add new slides or update existing presentations with structured,
            branded content.
          </li>
          <li>
            • Apply consistent, professional design themes with your brand's
            fonts, colors, and logos.
          </li>
          <li>
            • Summarize or extract key insights from uploaded documents or
            existing presentations.
          </li>
        </ul>

        <p className="mt-6 text-slate-600">
          Whether you're building a startup toolkit, automating team workflows,
          or scaling enterprise comms, Zynth's API delivers professional-grade
          presentations at speed and scale.
        </p>

        <details className="mt-8">
          <summary className="text-xl font-semibold text-slate-800 cursor-pointer hover:text-slate-900 [&::-webkit-details-marker]:text-black">
            The API includes three powerful endpoints:
          </summary>

          <div className="mt-6 space-y-6">
            <details className="border-l-4 border-blue-400 pl-4">
              <summary className="text-lg font-semibold text-slate-800 mb-2 cursor-pointer hover:text-slate-900 [&::-webkit-details-marker]:text-black">
                1. Generate Full Presentation
              </summary>
              <div className="mt-4">
                <p className="my-2 text-slate-600">
                  Generate an entire presentation based on a simple text input
                  or document upload.
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Endpoint:</strong>{" "}
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                  https://api.zynth.ai/api/v1/data/appscripts/generate-and-get-url/:orgId
                  </code>
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Input:</strong> A paragraph or a document upload with
                  a set of instructions describing the entire presentation.
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Output:</strong> A structured, multi-slide
                  presentation draft.
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Use Case:</strong> Quickly create a full deck for
                  investor pitches, business overviews, marketing plans, etc.
                </p>
              </div>
            </details>

            <details className="border-l-4 border-green-400 pl-4">
              <summary className="text-lg font-semibold text-slate-800 mb-2 cursor-pointer hover:text-slate-900 [&::-webkit-details-marker]:text-black">
                2. Generate Slide by Builder
              </summary>
              <div className="mt-4">
                <p className="my-2 text-slate-600">
                  Create a custom slide with full control over content structure
                  and data. Supports multiple slide types including Points,
                  Statistics, Timeline, Images, Tables, People, Graphs, and
                  TextandImage slides. The user manually enters the slide
                  content which gives full control without AI involvement in
                  content generation.
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Endpoint:</strong>{" "}
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                  https://api.zynth.ai/api/v1/data/appscripts/slidecustom/generate-document-slide/:orgId/:section
                  </code>
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Input:</strong> Structured input based on a selected
                  slide type (e.g., points, tables, timeline, etc.).
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Output:</strong> A slide tailored exactly to the given
                  structure and data.
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Use Case:</strong> Ideal for platforms needing high
                  control over slide formats — like dashboards, report tools, or
                  education platforms.
                </p>
              </div>
            </details>

            <details className="border-l-4 border-purple-400 pl-4">
              <summary className="text-lg font-semibold text-slate-800 mb-2 cursor-pointer hover:text-slate-900 [&::-webkit-details-marker]:text-black">
                3. Generate Slide by Narrative
              </summary>
              <div className="mt-4">
                <p className="my-2 text-slate-600">
                  Generate a slide by providing a slide narrative. The user
                  provides a custom prompt or instruction, and the system
                  generates content and slide based on that input.
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Endpoint:</strong>{" "}
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                  https://api.zynth.ai/api/v1/data/appscripts/slidenarrative/:orgId
                  </code>
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Input:</strong> A narrative or paragraph describing
                  the content of a single slide along with the preferred slide
                  type.
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Output:</strong> One slide with structured layout and
                  design based on the content.
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Use Case:</strong> Perfect for generating individual
                  slides dynamically based on meeting notes, reports, or quick
                  ideas.
                </p>
              </div>
            </details>
          </div>
        </details>
      </div>
    </section>
  );
};

export default Overview;
